// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';

import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { useCurrentReceipt } from '@tectonics/prs/receipts';
import { getInstalmentsPlan } from '@tectonics/prs/receipts/receipt';
import { getInstalmentsNumber, getAmountPartialFirst } from '@tectonics/prs/receipts/receipt/instalments-plan';

import {
  SideDisplayValueItem,
  SideDisplayValueItemTitle,
  SideDisplayValueItemValue,
} from './side-display-values';

import { amountToCurrencyString } from '@tectonics/prs/money';
import { getMoneyFormat } from '../../commons/money-format';

import { Separator } from './separator';

export function DisplaySelectedInstalment(): Node {
  const currentReceipt = useCurrentReceipt();
  const instalmentsPlan = getInstalmentsPlan(currentReceipt);

  if (!instalmentsPlan || getInstalmentsNumber(instalmentsPlan) === 1) {
    return null;
  }

  return (
    <>
      <Separator />

      <Root>
        <SideDisplayValueItem data-testid="display_selected_instalment">
          <SideDisplayValueItemTitle>
            <InstalmentsLabelText />
          </SideDisplayValueItemTitle>
          <SideDisplayValueItemValue>
            <InstalmentsValueText />
          </SideDisplayValueItemValue>
        </SideDisplayValueItem>
      </Root>
    </>
  );
}

function InstalmentsLabelText(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return 'Parcelamento';

    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'pe':
    case 'uy':
    case 'py':
    case 'cr':
    case 'pa':
    case 'sv':
    case 'gt':
    case 'do':
    case 'mx':
      return 'Cuotas';

    default:
      throw new Error(`Credit card instalments: label not mapped for country: ${country}`);
  }
}

function InstalmentsValueText(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  const currentReceipt = useCurrentReceipt();
  const instalmentsPlan = getInstalmentsPlan(currentReceipt);
  const formatAmount = amountToCurrencyString(getMoneyFormat(country));

  if (!instalmentsPlan) {
    return null;
  }

  switch (country) {
    case 'br':
    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'pe':
    case 'uy':
    case 'py':
    case 'pa':
    case 'sv':
    case 'gt':
    case 'do':
    case 'cr':
      return `${getInstalmentsNumber(instalmentsPlan)}x de ${formatAmount(getAmountPartialFirst(instalmentsPlan))}`;

    case 'mx':
      return `${getInstalmentsNumber(instalmentsPlan)} meses de ${formatAmount(getAmountPartialFirst(instalmentsPlan))}`;

    default:
      throw new Error(`InstalmentsValueText: text not mapped for country: ${country}`);
  }
}

const Root = styled.div`
  padding: 16px;
  color: ${props => props.theme.colors.primary(10)};
`;
